import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useTranslation } from 'react-i18next';
import QuickDateButtons from './QuickDateButtons';
import { addHours } from 'date-fns';

interface ReservationDetailsFormProps {
  onDetailsSubmit: (details: {
    email: string;
    startDate: Date;
    endDate: Date;
    purpose: string;
    notes: string;
  }) => void;
  onOpenCalendar?: () => void;
  initialEmail?: string;
  initialStartDate?: Date | null;
  initialEndDate?: Date | null;
  initialPurpose?: string;
  initialNotes?: string;
}

// Fonction pour générer les dates par défaut : J+1 à 8h00 et J+1 à 10h00 (2h de durée)
const getDefaultDates = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);

  const tomorrowEnd = new Date(tomorrow);
  tomorrowEnd.setHours(10, 0, 0, 0); // 2 heures plus tard

  return { defaultStart: tomorrow, defaultEnd: tomorrowEnd };
};

const ReservationDetailsForm: React.FC<ReservationDetailsFormProps> = ({
  onDetailsSubmit,
  onOpenCalendar,
  initialEmail = '',
  initialStartDate = null,
  initialEndDate = null,
  initialPurpose = '',
  initialNotes = ''
}) => {
  const { t } = useTranslation();
  const { defaultStart, defaultEnd } = getDefaultDates();

  const [email, setEmail] = useState(initialEmail);
  const [startDate, setStartDate] = useState<Date | null>(initialStartDate || defaultStart);
  const [endDate, setEndDate] = useState<Date | null>(initialEndDate || defaultEnd);
  const [purpose, setPurpose] = useState(initialPurpose);
  const [notes, setNotes] = useState(initialNotes);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Ref pour éviter de recalculer lors du montage initial ou lors de l'utilisation des boutons rapides
  const isInitialMount = useRef(true);
  const skipAutoUpdate = useRef(false);

  // Recalculer la date de fin (+2h) quand la date de départ change manuellement
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Ne pas recalculer si c'est un bouton rapide qui a modifié les dates
    if (skipAutoUpdate.current) {
      skipAutoUpdate.current = false;
      return;
    }

    if (startDate) {
      const newEndDate = addHours(startDate, 2);
      setEndDate(newEndDate);
    }
  }, [startDate]);

  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);

    if (newEmail && !validateEmail(newEmail)) {
      setEmailError(t('reservation:errors.invalidEmail'));
    } else {
      setEmailError(null);
    }
  };

  const handleQuickDateSelection = (start: Date, end: Date) => {
    // Désactiver le recalcul automatique car les boutons rapides définissent déjà les deux dates
    skipAutoUpdate.current = true;
    setStartDate(start);
    setEndDate(end);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!email) {
      setError(t('reservation:errors.emailRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('reservation:errors.invalidEmail'));
      return;
    }

    if (!purpose || purpose.trim() === '') {
      setError(t('reservation:errors.purposeRequired', 'Le motif de déplacement est requis'));
      return;
    }

    if (!startDate || !endDate) {
      setError(t('reservation:errors.datesRequired'));
      return;
    }

    if (endDate <= startDate) {
      setError(t('reservation:errors.endDateBeforeStartDate'));
      return;
    }

    // Soumettre les détails au parent
    onDetailsSubmit({
      email,
      startDate,
      endDate,
      purpose,
      notes
    });
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('reservation:publicPage.steps.dateAndInfo', 'Dates et informations')}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label={t('reservation:publicPage.emailPlaceholder')}
              fullWidth
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              required
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={t('reservation:publicPage.startDate')}
              value={startDate}
              onChange={setStartDate}
              disablePast
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <DateTimePicker
              label={t('reservation:publicPage.endDate')}
              value={endDate}
              onChange={setEndDate}
              disablePast
              minDateTime={startDate ? new Date(startDate.getTime() + 30 * 60 * 1000) : undefined}
              slotProps={{
                textField: {
                  fullWidth: true,
                  required: true
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <QuickDateButtons
              startDate={startDate}
              onDatesSelected={handleQuickDateSelection}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('reservation:publicPage.purpose', 'Motif de déplacement') + ' *'}
              fullWidth
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              required
              placeholder={t('reservation:publicPage.purposePlaceholder', 'visite client')}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label={t('reservation:publicPage.notes')}
              fullWidth
              multiline
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>

          {onOpenCalendar && (
            <Grid item xs={12}>
              <Button
                variant="text"
                startIcon={<VisibilityIcon />}
                onClick={onOpenCalendar}
                sx={{ textTransform: 'none' }}
              >
                {t('reservation:publicPage.viewCalendar', 'Voir la semaine (lecture seule)')}
              </Button>
            </Grid>
          )}

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
            >
              {t('common.next')}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Paper>
  );
};

export default ReservationDetailsForm;
